import { DeleteOutline } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import audioIcon from "./assets/audioIcon.svg";
import downloadIcon from "./assets/downloadIcon.svg";
import pdfIcon from "./assets/pdfIcon.svg";
import videoIcon from "./assets/videoIcon.svg";
import { useStyles } from "./styles";

export type UploadedResource = {
  resourceType: "video" | "audio" | "pdf";
  resourceName?: string;
  resourceSize?: number;
  url?: string;
  resourcePath?: string;
  isDeleted?: boolean;
};

const ICONS: Record<
  UploadedResource["resourceType"],
  { color: string; glyph: string }
> = {
  video: { color: "#3D63E6", glyph: videoIcon },
  audio: { color: "#963DE6", glyph: audioIcon },
  pdf: { color: "#F24474", glyph: pdfIcon },
};

const formatSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${Math.round(mb)} MB`;
  const kb = bytes / 1024;
  return `${Math.round(kb)} KB`;
};

type Props = {
  resources: UploadedResource[];
  onDelete?: () => void;
  className?: string;
};

export const UploadedFileCell = ({ resources, onDelete, className }: Props) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const resource = (resources ?? []).find(
    (r) => !r?.isDeleted && (r?.url || r?.resourcePath)
  );

  if (!resource) {
    return <div>{"-"}</div>;
  }

  const { color, glyph } = ICONS[resource.resourceType];
  const name =
    resource.resourceName || resource.url?.split("/").pop() || "";
  const size = formatSize(resource.resourceSize);

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.fileInfo}>
        <div className={styles.iconWrap} style={{ backgroundColor: color }}>
          <img src={glyph} alt={resource.resourceType} className={styles.glyph} />
        </div>
        <div className={styles.text}>
          <span className={clsx(styles.name, onDelete && styles.nameLink)}>
            {name}
          </span>
          {size && <span className={styles.size}>{size}</span>}
        </div>
      </div>
      {onDelete ? (
        <Tooltip title={t("TOOLTIP.DELETE")}>
          <IconButton
            size="small"
            onClick={onDelete}
            className={styles.deleteBtn}
            aria-label="delete"
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : resource.url ? (
        <Tooltip title={t("TOOLTIP.DOWNLOAD")}>
          <a
            href={resource.url}
            target="_blank"
            rel="noreferrer"
            className={styles.downloadLink}
          >
            <img
              src={downloadIcon}
              alt="downloadIcon"
              className={styles.download}
            />
          </a>
        </Tooltip>
      ) : null}
    </div>
  );
};
